import { boolean, number, optional, optionalString, string, zodLess, ZodLessInfer } from '@kaiizen/zod-less';
import { ApiUserSchema } from './RecordTypes';

// *******************************************************************
// Auth API
// *******************************************************************
export const SessionStatusSchema = zodLess('pending|authorized|invalid|deactivated|expired');
export type SessionStatus = ZodLessInfer<typeof SessionStatusSchema>;

export const AuthValidateResponseSchema = zodLess({
  isValid: boolean,
}, { ref: 'validate', description: 'Response from authentication endpoints.' });

export const AuthInitiateRequestPayloadSchema = zodLess({
  phoneNumber: string,
  messageType: 'sms|whatsapp',
}, { ref: 'initiateReceiveSmsAuth', description: 'Request to initiate authentication.' });

export const AuthInitiateResponseSchema = zodLess({
  sessionId: string,
  status: SessionStatusSchema,
  authCode: string.describe('user must send this code to Kaiizen via SMS or WhatsApp to validate their session'), // user must send this code to the user via SMS or WhatsApp
  messageUrl: string.describe(
    'A URL containing the authCode that automatically opens the local SMS or WhatsApp app with and pre-populates the message with the authCode to validate their session',
  ), // user can use this URL to send the authCode via WhatsApp or SMS with one click
  verificationToken: string.describe(
    'Required for the client to complete the authentication handshake after the user has validated their session with the authCode',
  ),
}, { ref: 'authInitResponse', description: 'Response from authentication endpoints.' });

export const AuthCheckSessionRequestPayloadSchema = zodLess({
  phoneNumber: string,
  verificationToken: string,
}, {
  ref: 'checkAuthSession',
  description:
    'Request to check authentication session status. The user must have already validated their session with the authCode. The phone number, sessionId, and verificationToken must all match the complete the handshake.',
});

export const AuthCheckSessionResponseSchema = zodLess({
  status: SessionStatusSchema,
  token: optionalString,
  sessionId: optionalString,
  user: optional(ApiUserSchema),
  orgId: optionalString,
}, { ref: 'authResponse', description: 'Response from authentication endpoints.' });

export const AuthGetQrCodeQuerySchema = zodLess({
  phoneNumber: string,
  verificationToken: string,
  messageType: 'sms|whatsapp',
}, { ref: 'getQrCodeRequest', description: 'Request to get a QR code for authentication.' });

export const AuthGetQrCodeResponseSchema = zodLess({
  qrCodeUrl: string,
}, { ref: 'getQrCodeResponse', description: 'Response from authentication endpoints.' });

export const InitiateAuthRequestPayloadSchema = zodLess({
  phoneNumber: string,
  messageType: string,
});

export const CheckSessionRequestPayloadSchema = zodLess({
  verificationToken: string,
  phoneNumber: string,
});

export const bearer = string;

// SMS Authentication schemas
export const AuthInitiateSmsRequestPayloadSchema = zodLess({
  phoneNumber: string,
  messageType: 'sms|whatsapp',
}, { ref: 'initiateSendSmsAuth', description: 'Request to initiate authentication where Kai sends a verification code via SMS or WhatsApp.' });
export type AuthInitiateSmsRequestPayload = ZodLessInfer<typeof AuthInitiateSmsRequestPayloadSchema>;

export const AuthInitiateEmailRequestPayloadSchema = zodLess({
  email: string,
}, { ref: 'initiateSendEmailAuth', description: 'Request to initiate authentication where Kai sends a verification code via email.' });

export const AuthInitiateSmsResponseSchema = zodLess({
  success: boolean,
}, { ref: 'initiateSmsAuthResponse', description: 'Response from SMS authentication initiation.' });

export const AuthValidateSmsRequestPayloadSchema = zodLess({
  phoneNumber: string,
  code: string,
}, { ref: 'validateSendSmsAuth', description: 'Request to validate SMS authentication code.' });
export type AuthValidateSmsRequestPayload = ZodLessInfer<typeof AuthValidateSmsRequestPayloadSchema>;

export const AuthValidateEmailRequestPayloadSchema = zodLess({
  email: string,
  code: string,
}, { ref: 'validateSendEmailAuth', description: 'Request to validate email authentication code.' });

// Expo Push Notifications
export const AuthSyncExpoTokensRequestPayloadSchema = zodLess({
  expoTokens: [string],
}, { ref: 'syncExpoTokens', description: 'Request to sync Expo push notification tokens for the user.' });

export const AuthSyncExpoTokensResponseSchema = zodLess({
  success: boolean,
  tokensCount: number,
}, { ref: 'syncExpoTokensResponse', description: 'Response from syncing Expo tokens.' });

export const AuthGetExpoTokensResponseSchema = zodLess({
  expoTokens: [string],
}, { ref: 'getExpoTokensResponse', description: "Response containing user's Expo push notification tokens." });

// Delete Account
export const AuthDeleteAccountResponseSchema = zodLess({
  success: boolean,
  deletedAt: string,
}, { ref: 'deleteAccountResponse', description: 'Response from deleting user account.' });
